import { useEffect, useMemo, useRef, useState } from "react"

import { App, Flex, Typography } from "antd"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import Button from "../../../../../../Components/Button"
import Modal from "../../../../../../Components/Modal"
import Select from "../../../../../../Components/Select"
import Spinner from "../../../../../../Components/Spinner"
import { generatePreview, getPreviewPage } from "../../../../api/services"
import { getReasonCategories } from "../../../../redux/disputes.thunks"
import { groupByNetworkCategory } from "../../../AllPage/constants/categoryGroups"
import { REASON_CATEGORY_FALLBACK } from "../../../AllPage/constants/reasonCategories"
import { selectActiveBuildingBlocks } from "../../redux/configure.slice"

import styles from "./previewModal.module.less"

const { Text } = Typography

// Stable identity for the missing-options case (useSelector compares with ===).
const EMPTY = []

const ISSUER_OPTIONS = [
  { value: "visa", label: "Visa" },
  { value: "mastercard", label: "Mastercard" },
  { value: "amex", label: "Amex" },
  { value: "discover", label: "Discover" }
]

// Anti-leak preview rendering, identical to the dispute preview (DetailPage): the
// clean, submittable PDF is never sent to the browser. The backend renders the
// preview to flattened, watermarked PNG pages; we fetch each page and decode it
// straight to an ImageBitmap (deliberately NOT an object URL, so there is no
// `blob:` URL in the DOM to lift) and paint it onto a <canvas> (no <img src> to
// pull). So the merchant can scroll the document but cannot download, print,
// copy text, or grab the file.
const fetchPreviewPageBitmap = async (page) => {
  const res = await getPreviewPage(page)
  if (!res?.ok || !res?.data) return null
  const raw = res.data
  const blob = raw instanceof Blob ? raw : new Blob([raw], { type: "image/png" })
  try {
    return await createImageBitmap(blob)
  } catch (e) {
    return null
  }
}

// Renders one decoded page onto a canvas (no <img src>, no extractable URL,
// context menu disabled) - same component shape as the dispute preview.
const PreviewPageCanvas = ({ bitmap, index, t }) => {
  const ref = useRef(null)
  useEffect(() => {
    const c = ref.current
    if (!c || !bitmap) return
    c.width = bitmap.width
    c.height = bitmap.height
    c.getContext("2d").drawImage(bitmap, 0, 0)
  }, [bitmap])
  return (
    <canvas
      ref={ref}
      className="rd-page-img"
      role="img"
      aria-label={t("Evidence page _n_", { n: index + 1 })}
      onContextMenu={(e) => e.preventDefault()}
    />
  )
}

const PreviewModal = ({ open, onClose }) => {
  const dispatch = useDispatch()
  const isMobile = useSelector((s) => s.nav.isMobile)
  const reasonCategoryOptions = useSelector((s) => s.disputes.reasonCategories?.options) ?? EMPTY
  const buildingBlocks = useSelector(selectActiveBuildingBlocks)
  const { t } = useTranslation()
  const { notification } = App.useApp()
  const [category, setCategory] = useState("fraudulent")
  const [issuer, setIssuer] = useState("visa")
  const [generating, setGenerating] = useState(false)
  const [pages, setPages] = useState([]) // decoded ImageBitmaps (no blob URLs)
  const [pagesError, setPagesError] = useState(false)
  const pagesRef = useRef([])
  // Monotonic token so a slow page-load for an old Generate can't overwrite (or
  // leak) the images of a preview the merchant has since regenerated.
  const loadTokenRef = useRef(0)

  // Hydrate the category taxonomy on first open so the dropdown has the
  // canonical 14-option list rather than the local fallback.
  useEffect(() => {
    if (open && (!reasonCategoryOptions || reasonCategoryOptions.length === 0)) {
      dispatch(getReasonCategories())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const categoryOptions = useMemo(
    () =>
      reasonCategoryOptions && reasonCategoryOptions.length
        ? reasonCategoryOptions
        : REASON_CATEGORY_FALLBACK,
    [reasonCategoryOptions]
  )

  // Align the picker to the 4 card-network dispute categories (Phase 1): fold the
  // fine-grained reason categories under their network bucket (Fraud / Consumer
  // Disputes / Processing Errors / Authorization) so the list reads the way an
  // issuer classifies a dispute. Specific reason codes are Phase 2.
  const groupedCategoryOptions = useMemo(
    () =>
      groupByNetworkCategory(
        categoryOptions.map((o) => ({ value: o.value, label: t(o.label) })),
        t
      ),
    [categoryOptions, t]
  )

  // Playground: render the live evidence document against the merchant's Configure
  // building blocks + a sample dispute built from the chosen scenario (no backend).
  const sampleDispute = useMemo(
    () => ({
      processor_dispute_id: "du_PREVIEW_SAMPLE",
      reason_category: category,
      reason_category_label:
        (categoryOptions.find((o) => o.value === category) || {}).label || category,
      issuer,
      card_last4: "4242",
      amount: 129.0,
      currency: "USD",
      authorization_code: "074502",
      transaction_type: "Purchase",
      customer_full_name: "Sample Cardholder",
      customer_email: "cardholder@example.com",
      transaction_date: dayjs().subtract(24, "day").toISOString(),
      dispute_created_at: dayjs().subtract(10, "day").toISOString()
    }),
    [category, issuer, categoryOptions]
  )

  // Release the decoded page bitmaps on unmount.
  useEffect(() => {
    return () => {
      pagesRef.current.forEach((b) => b && b.close && b.close())
      pagesRef.current = []
    }
  }, [])

  const generate = async () => {
    const token = ++loadTokenRef.current
    setGenerating(true)
    setPagesError(false)
    try {
      const response = await generatePreview({
        reason_category: category, // backend resolves to a representative raw code
        issuer
        // No business_type: the backend uses the merchant's own industry from
        // Configure step 1, so the preview always reflects their real business.
      })
      // The backend renders + caches the watermarked preview and returns the page
      // count; bail on an error body before we try to load pages.
      if (!response?.ok || !response?.data) {
        notification.error({
          message: t("Couldn't generate the preview. Please try again.")
        })
        return
      }
      const count = Math.max(1, Number(response.data.page_count) || 1)
      const bitmaps = await Promise.all(
        Array.from({ length: count }, (_, i) => fetchPreviewPageBitmap(i))
      )
      // A newer Generate started while we were fetching: drop these results and
      // close the bitmaps so they don't leak or clobber the current preview.
      if (token !== loadTokenRef.current) {
        bitmaps.forEach((b) => b && b.close && b.close())
        return
      }
      // Release the previously-shown pages before swapping in the new ones.
      pagesRef.current.forEach((b) => b && b.close && b.close())
      const ok = bitmaps.filter(Boolean)
      pagesRef.current = ok
      setPages(ok)
      // We rendered the preview but its page images failed to load (network/auth):
      // that is a load failure, not "nothing to show", so offer a retry.
      setPagesError(ok.length === 0)
    } catch (e) {
      if (token === loadTokenRef.current) {
        notification.error({
          message: t("Couldn't generate the preview. Please try again.")
        })
      }
    } finally {
      if (token === loadTokenRef.current) setGenerating(false)
    }
  }

  const handleClose = () => {
    pagesRef.current.forEach((b) => b && b.close && b.close())
    pagesRef.current = []
    setPages([])
    setPagesError(false)
    onClose?.()
  }

  return (
    <Modal
      visible={open}
      handleClose={handleClose}
      // Mobile gets the full viewport: a fixed 1000px modal sat half off-screen at
      // 375px, with no way to reach its right-hand side.
      width={isMobile ? "100%" : 1000}
      destroyOnClose
      title={t("Preview your evidence PDF")}
    >
      <Flex vertical gap={12}>
        <Text type="secondary">
          {t(
            "Pick a dispute category and network. We render a sample evidence PDF using your current profile so you can see exactly what we would send."
          )}
        </Text>

        <Flex gap={12} wrap="wrap" align="center">
          <Select
            size="large"
            value={category}
            options={groupedCategoryOptions}
            onChange={setCategory}
            style={{ minWidth: 260 }}
          />
          <Select
            size="large"
            value={issuer}
            options={ISSUER_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
            onChange={setIssuer}
            style={{ minWidth: 180 }}
          />
          <Button
            type="primary"
            size="large"
            loading={generating}
            onClick={generate}
            text={pages.length ? t("Regenerate") : t("Generate preview")}
          />
        </Flex>

        <div className={styles.previewFrame} style={{ height: 600 }}>
          {generating ? (
            <Flex align="center" justify="center" style={{ width: "100%", height: "100%" }}>
              <Spinner />
            </Flex>
          ) : pages.length ? (
            // Watermarked pages painted onto <canvas> elements: scrollable, but no
            // selectable text, no links, no download, and no `blob:` URL in the DOM
            // that a merchant could open in a new tab to grab the clean page.
            <div className={styles.previewScroll}>
              <div className="rd-pages">
                {pages.map((b, i) => (
                  <PreviewPageCanvas key={i} bitmap={b} index={i} t={t} />
                ))}
              </div>
            </div>
          ) : pagesError ? (
            <Flex
              vertical
              align="center"
              justify="center"
              gap={8}
              style={{ width: "100%", height: "100%", padding: 24, textAlign: "center" }}
            >
              <Text strong>{t("Couldn't load the evidence pages")}</Text>
              <Button onClick={generate} text={t("Retry")} />
            </Flex>
          ) : (
            <Flex align="center" justify="center" style={{ width: "100%", height: "100%" }}>
              <Text type="secondary">
                {t("Pick a dispute scenario above and hit Generate to see your PDF.")}
              </Text>
            </Flex>
          )}
        </div>
      </Flex>
    </Modal>
  )
}

export default PreviewModal
